"use client";

import { cn } from "@/lib/utils";
import { motion } from "motion/react";
import Image from "next/image";
import { useRef } from "react";
import CountUp from "react-countup";
import { SanityImpact } from "@/sanity/lib/types";
import { urlFor } from "@/sanity/lib/image";

const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 220 },
  show: { opacity: 1, y: 0 },
};

interface ImpactCardsProps {
  cards: SanityImpact[];
}

export default function ImpactCards({ cards }: ImpactCardsProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <motion.div
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      transition={{ duration: 0.1 }}
      ref={containerRef}
      className="z-10 my-2 flex w-full flex-wrap items-center justify-center justify-items-center gap-4 lg:gap-6"
    >
      {cards.map((card, index) => (
        <motion.div
          key={index}
          className={cn(
            "relative flex w-full grow flex-col justify-between gap-4 overflow-hidden rounded-2xl rounded-br-[72px] px-6 py-3.5 shadow-md transition-all duration-1000 ease-in-out hover:scale-102 md:max-w-76 lg:h-56 lg:min-h-44 lg:max-w-96",
            {
              "bg-secondary-light text-secondary": card.suffix,
              "bg-primary-light text-primary": !card.suffix,
            },
          )}
          variants={item}
          transition={{ duration: 0.1 }}
        >
          <Image
            src={urlFor(card.icon).width(200).height(200).url()}
            alt={card.icon.alt || card.subtitle}
            width={200}
            height={200}
            className={cn(
              "absolute right-4 -bottom-0 h-28 w-auto opacity-15 lg:-right-2 lg:-bottom-4 lg:h-50",
              {
                "filter-secondary": card.suffix,
                "filter-primary": !card.suffix,
              },
            )}
          />
          <div className="text-5xl font-semibold transition-all duration-1000 ease-in-out lg:text-8xl">
            <CountUp
              end={card.title}
              duration={5}
              enableScrollSpy
              scrollSpyOnce
              suffix={card.suffix || undefined}
              separator=" "
            />
          </div>
          <div className="flex h-[2lh] flex-col justify-end text-2xl font-medium md:mr-12 lg:h-auto">
            {card.subtitle}
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}
